import React from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import "./MatchReasonsPopover.scss";

const REASON_GROUPS = [
  "matched",
  "partial",
  "missing",
  "must_haves_matched",
  "must_haves_missing",
];

// Nice-to-have chips read from whichever side the viewer owns: on a
// Want-Listing's matches the viewer is the buyer's agent, on a listing's
// matches they hold the inventory.
const CHIP_LABELS = {
  buyer: { matched: "You want, this has:", missing: "You want, this lacks:" },
  seller: {
    matched: "They want, you have:",
    missing: "They want, you don't have:",
  },
};

const hasAnyReason = (reasons) =>
  !!reasons && REASON_GROUPS.some((group) => reasons[group]?.length > 0);

const ReasonList = ({ items, variant, icon }) => {
  if (!items?.length) return null;
  return (
    <ul
      className={`match-reasons-popover__list match-reasons-popover__list--${variant}`}
    >
      {items.map((reason, i) => (
        <li key={reason.key || `${variant}-${i}`}>
          <span className="match-reasons-popover__icon" aria-hidden="true">
            {icon}
          </span>
          <span>{reason.text}</span>
        </li>
      ))}
    </ul>
  );
};

const ChipSection = ({ label, items, variant }) => {
  if (!items?.length) return null;
  return (
    <div className="match-reasons-popover__chip-section">
      <p className="match-reasons-popover__chip-label">{label}</p>
      <div className="match-reasons-popover__chips">
        {items.map((text, i) => (
          <span
            key={`${variant}-${i}`}
            className={`match-reasons-popover__chip match-reasons-popover__chip--${variant}`}
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  );
};

const MatchReasonsPopover = ({
  reasons,
  score,
  children,
  placement = "bottom",
  perspective = "buyer",
}) => {
  if (!hasAnyReason(reasons)) return children ?? null;

  const numericScore = Math.round(parseFloat(score));
  const showScore = Number.isFinite(numericScore);
  const chipLabels = CHIP_LABELS[perspective] || CHIP_LABELS.buyer;

  const popover = (
    <Popover className="match-reasons-popover">
      <Popover.Header className="match-reasons-popover__header">
        {showScore && (
          <span className="match-reasons-popover__score">{numericScore}%</span>
        )}
        {reasons.summary && (
          <span className="match-reasons-popover__summary">
            {reasons.summary}
          </span>
        )}
      </Popover.Header>
      <Popover.Body className="match-reasons-popover__body">
        <ReasonList items={reasons.matched} variant="matched" icon="✓" />
        <ChipSection
          label={chipLabels.matched}
          items={reasons.must_haves_matched}
          variant="yes"
        />
        <ReasonList items={reasons.partial} variant="partial" icon="~" />
        <ChipSection
          label={chipLabels.missing}
          items={reasons.must_haves_missing}
          variant="no"
        />
        <ReasonList items={reasons.missing} variant="missing" icon="✗" />
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      placement={placement}
      trigger={["hover", "focus", "click"]}
      rootClose
      delay={{ show: 150, hide: 200 }}
      overlay={popover}
    >
      <span
        className="match-reasons-popover__trigger"
        role="button"
        tabIndex={0}
        aria-label="Why this matched"
        // Match cards are clickable; stop the badge from navigating on tap.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default MatchReasonsPopover;
